/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, {useEffect, useRef, useState} from "react";
import {PropTypes} from "prop-types";
import styled, {css} from "styled-components";
import {FiraSansBold, FontWeight, Text} from '../Text';
import Icon from "../Icon";
import Colors from "constants/Colors";
import Label from "./Label";
import ValidationError from "./ValidationError";
import Spinner, {SpinnerSize} from "../Spinner";
import {Breakpoints} from "../../constants/Breakpoints";
import { handleScrollWhenInputIsFocused } from '@fitinsur/utils/src/scroll-input-focus';

export const InputSize = {
  SMALL: 1,
  NORMAL: 2,
};

const propTypes = {
  response: PropTypes.bool,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  leftRender: PropTypes.func,
  rightRender: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  format: PropTypes.func,
  parse: PropTypes.func,
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  isRequired: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onPressEnter: PropTypes.func,
  canGenerate:PropTypes.bool,
  fieldProps: PropTypes.any,
  smallInput: PropTypes.bool,
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  inverseBlue: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(InputSize)),
  valueCalendar: PropTypes.any,
  onShowChatInsurer: PropTypes.any,
  formatTextHtml: PropTypes.bool
};

const defaultProps = {
  canGenerate:true,
  response: false,
  type: "text",
  placeholder: "",
  leftIcon: null,
  rightIcon: null,
  leftRender: null,
  rightRender: null,
  label: null,
  validation: true,
  format: (value) => value,
  parse: (value) => value,
  isLoading: false,
  isRequired: false,
  tabIndex: 0,
  onChange: (value) => value,
  onBlur: (value) => value,
  onFocus: (value) => value,
  onPressEnter: (value) => value,
  fieldProps: {},
  smallInput: false,
  readOnly: false,
  inline: false,
  inverseBlue: false,
  size: InputSize.NORMAL,
  valueCalendar: null,
  onShowChatInsurer: null,
  formatTextHtml: false
};

// Mixins
const errorInputMixin = css`
  box-shadow: 0 0 0 3px ${Colors.DANGER};

  &:focus {
    box-shadow: 0 0 0 3px ${Colors.DANGER};
  }
`;
const inverseBlueInputMixin = css`
  background-color: transparent;
  color: ${Colors.WHITE};

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background-color: ${Colors.PRIMARY};
  }
  &:active:not(:disabled) {
    background-color: ${Colors.PRIMARY};
  }
`;
const inverseBlueContainerMixin = css`
  background: none;
  border-color: ${Colors.WHITE};

  & input {
    ${inverseBlueInputMixin}

    &[disabled] {
      background: none;
    }
  }
`;
const smallInputMixin = css`
  height: 32px;
`;

// Components
export const InputContainer = styled.div`
  box-sizing: border-box;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  box-shadow: 0 0 0 1px ${Colors.GRAY[3]};
  transition: box-shadow 0.15s ease;
  margin-bottom: 10px;
  ${({ isFocused }) =>
    isFocused &&
    css`
      box-shadow: 0 0 0 3px ${Colors.SECONDARY};
      
      ${Input} {
        background: ${Colors.GRAY[5]}
        box-shadow: inset 0px 2px 6px rgba(0, 0, 0, 0.2);
      }
    `}
  ${({ inline }) =>
    inline &&
    css`
      min-width: 115px;
      width: 80%;
    `}
  
  ${({ meta }) => (meta && meta.touched && meta.error ? errorInputMixin : "")}

  ${({ inverseBlue }) => inverseBlue && inverseBlueContainerMixin}
  
  @media screen and (min-width: ${Breakpoints.md}) {
    margin-bottom: 0;
  }
`;
export const Option = styled.li`
  list-style: none;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${Colors.GRAY[3]};

  ${({ selected }) =>
    selected &&
    css`
      background-color: ${Colors.Alpha(Colors.SECONDARY, 0.7)};
    `}

  &:hover {
    background-color: ${Colors.GRAY[4]};
  }
`;
export const OptionsContainer = styled.ul`
  position: absolute;
  z-index: 2000;
  top: ${({ filterModal }) => (filterModal ? "46px" : "66px")};
  left: ${({ filterModal }) => (filterModal ? "0px" : "12px")};
  max-height: 300px;
  width: ${({ filterModal }) => (filterModal ? "100%" : "calc(100% - 26px);")};
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-left: 3px solid ${Colors.SECONDARY};
  border-right: 3px solid ${Colors.SECONDARY};
  border-bottom: 3px solid ${Colors.SECONDARY};
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  
  @media (max-width: 768px) {
    width: 93%;
  }
`;
export const Input = styled.input`
  width: ${({ smallInput }) => (smallInput ? "40px" : "100%")};
  min-width: 0;
  padding: 4px;
  border: 0;
  background: white;
  appearance: none;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  outline: none;
  box-sizing: border-box;
  text-align: ${({ alignCenter }) => (alignCenter ? "center" : "initial")};
  ${FiraSansBold}
  transition: background 0.15s ease, box-shadow 0.15s ease;
  &::placeholder {
    color: ${Colors.GRAY[3]};
  }
  &[disabled] {
    background-color: ${Colors.GRAY[4]};
    color: ${Colors.GRAY[0]};
  }
  ${({ size }) => size && _getInputSizeMixin(size)}
  
  height: 42px;
  min-height: unset;
  
  @media screen and (min-width: ${Breakpoints.sm}) {
    height: 42px;
    width: ${({ onShowChatInsurer }) => onShowChatInsurer ? '100%' : null};
  }

  @media screen and (max-width: ${Breakpoints.xl}) {
    height: 42px;
    width: ${({ onShowChatInsurer }) => onShowChatInsurer ? '100%' : null}; 
  }
`;

export const IconButton = styled.div`
  tabIndex: -1,
  padding: 0;
  cursor: default;
  & ${Icon} {
    font-size: 16px;
    color: ${Colors.GRAY[3]};
    margin: 0 12px;
    
  }
`;
const InputSpinner = styled(Spinner)`
  margin: 0 12px;
`;
const Wrapper = styled.div`
  ${({ inline }) =>
    inline &&
    css`
      display: flex;
    `}
  ${({ disabled, inverse }) =>
    disabled &&
    inverse &&
    css`
      opacity: 0.3;
    `}
`;

const _getInputSizeMixin = (size) => {
  if (size === InputSize.SMALL) {
    return smallInputMixin;
  }

  return "";
};
const SearchField = ({
  response,
  type,
  placeholder,
  disabled,
  readOnly,
  leftIcon,
  leftIconClick,
  rightIcon,
  rightIconClick,
  leftRender,
  rightRender,
  isLoading,
  label,
  field,
  format,
  parse,
  size,
  setLabelMinHeight,
  fieldProps,
  isRequired,
  tabIndex,
  classname,
  form: { setFieldValue, setFieldTouched, touched, errors },
  inputProps,
  validationMinHeight,
  validation,
  hideLabelAsterisk,
  smallInput,
  canGenerate,
  alignCenter,
  inline,
  inverseBlue,
  onInputRef,
  onChange,
  onBlur,
  onFocus,
  onPressEnter,
  onSearchSelect,
  searchList,
  filterModal,
  list,
  onShowChatInsurer,
  formatTextHtml,
  /*  valueCalendar, */
  ...props
}) => {
  const inputRef = useRef(null);
  const selectionStart = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  /* const [showCalendar, setCalendar] = useState(false); */
  const [isOpen, setIsOpen] = useState(false);

  

  useEffect(() => {
    if (
      inputRef &&
      inputRef.current &&
      inputRef.current.selectionStart &&
      (selectionStart.current || selectionStart.current === 0)
    ) {
      inputRef.current.selectionStart = selectionStart.current;
      inputRef.current.selectionEnd = selectionStart.current;
    }
  }, []);

  var touchedList;
  var touchedItem;
  var errorList;
  var errorItem;
  const metaFields = field.name.split('.');
  if(metaFields.length == 3){
    touchedList = touched[metaFields[0]]
    if(touchedList){
      touchedItem = touchedList[parseInt(metaFields[1])]
    }
    errorList = errors[metaFields[0]]
    if(errorList){
      errorItem = errorList[parseInt(metaFields[1])]
    }
   
  
  }
  
  const meta = {
    
    touched: metaFields.length == 3 && touchedItem ? touchedItem[metaFields[2]] || touched[field.name]: touched[field.name],
    error: metaFields.length == 3 && errorItem ? errorItem[metaFields[2]] || errors[field.name]: errors[field.name],
    isFocused,
    isOpen
  };

  const _onFocus = () => {
    setIsFocused(true);
  };

  const _onBlur = () => {
    setIsFocused(false);
  };

  const [selectedResponse,setSelectedResponse] = useState(true);

  useEffect(() => {
    const close = (e) => {
      if(e.keyCode === 27){
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [])

  return (
    <Wrapper
      inline={inline}
      disabled={disabled}
      inverse={inverseBlue}
      {...props}
    /*   onFocus={() => {
        setIsOpen(true);

      }}
      onBlur={() => {
        setTimeout(()=>{ setIsOpen(false)},150)
       

      }} */
    >
      {(label || setLabelMinHeight) && (
        <Label
          meta={meta}
          isRequired={isRequired}
          weight={FontWeight.SemiBold}
          setMinHeight={setLabelMinHeight}
          inline={inline}
          inverse={inverseBlue}
          hideLabelAsterisk={hideLabelAsterisk}
        >
          {label}
        </Label>
      )}
      <InputContainer
       
        meta={meta}
        isFocused={meta.isFocused}
        inline={inline}
        inverseBlue={inverseBlue}
        
      >
        {leftIcon && (
          <IconButton onClick={leftIconClick}>
            <Icon name={leftIcon} />
          </IconButton>
        )}
        {leftRender && leftRender()}
        <Input
        /*   required={isRequired} */
          autoComplete={'off'}
          ref={(ref) => {
            inputRef.current = ref;
            if (onInputRef) onInputRef(ref);
          }}
          size={size}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={tabIndex}
          {...field}
          onKeyDown={(e) => {
            if (e.key.toLowerCase() === "enter") {
              onPressEnter(field.value);
            }
          }}
          onFocus={(e) => {
            setIsOpen(true);
            if(!canGenerate){
              if(!selectedResponse){
                setFieldValue(field.name, '');
               }
            }
           
            handleScrollWhenInputIsFocused(e);
            onFocus(field.value);
            _onFocus();
          }}
          onBlur={() => {
            
            if(!canGenerate){
              if(!selectedResponse){
                setFieldValue(field.name, '');
               }
            }
            setIsOpen(false)
           /* setTimeout(()=>{ },500) */
            onBlur(field.value, this);
            _onBlur();

            if (!meta.touched) {
              setFieldTouched(field.name, true);
            }
          }}
          smallInput={smallInput}
          inverseBlue={inverseBlue}
          alignCenter={alignCenter}
          onShowChatInsurer={onShowChatInsurer}
          formatTextHtml={formatTextHtml}
          value={props.value}
          onChange={(e) => {
            
            let currentSelection = e.target.selectionStart;
            const event = { ...e };
            const origValue = e.target.value;
            const maskedPrevValue = format(field.value) || "";

            // Note - we call format function to clear the input value
            // to remove exceeded characters
            //if (type!=="file"){
            event.target.value = parse(format(origValue));
            //}
            const maskedValue = format(event.target.value);

            const diffCaretPosistion =
              maskedValue.length - maskedPrevValue.length;
            currentSelection += diffCaretPosistion > 1 ? diffCaretPosistion : 0;

            // Temporary comment because it doesn't changes the value based on the mask
            // field.onChange(event);
            //if (type!=="file"){
            setFieldValue(field.name, parse(format(origValue)));
            onChange(parse(format(origValue)));
            setSelectedResponse(false);
            //} else {
            //  setFieldValue(field.name, event.currentTarget.files[0]);
            //}
            selectionStart.current = currentSelection;
          }}
          {...fieldProps}
        />

        {!rightIcon?
          <IconButton onClick={rightIconClick}>
            <Icon name={"search"} />
          </IconButton> 
          :
          <IconButton onClick={rightIconClick}>
          </IconButton> 
        }
        
        {rightRender && rightRender()}
        {isLoading && <InputSpinner size={SpinnerSize.SMALL} />}
        {isOpen && searchList?.length > 0 && (
          <OptionsContainer filterModal={filterModal}
         >
            {searchList.map((x, i) => (
              <Option key={`opt-${x.personName || x.value}-${i}`}
                onMouseDown={() => {
                  setIsOpen(false);
                  !formatTextHtml && setFieldValue(field.name, x.personName || x.value );
                  !formatTextHtml && onChange(x.personName || x.value);
                  onSearchSelect(x);
                  setSelectedResponse(true);
                }}
              >
                {formatTextHtml ? 
                  <Text dangerouslySetInnerHTML={{__html: x.personName || x.value}}/>
                  :  
                  <Text weight={FontWeight.Regular} >
                    {x.personName || x.value}
                  </Text>}
              </Option>
            ))}
          </OptionsContainer>
        )}
      </InputContainer>
      {validation && (
        <ValidationError
          meta={meta}
          validationMinHeight={validationMinHeight}
        />
      )}
    </Wrapper>
  );
};

SearchField.propTypes = propTypes;
SearchField.defaultProps = defaultProps;

export default SearchField;
